import { DEFAULT_DATE_FORMAT_FNS, FilterKeys, GetParentsQueryFilters } from '@schooly/api';
import {
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { Genders, InviteStatus, Nationalities, SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';

export type UseParentsFiltersProps = {
  schoolId: string;
  filters: GetParentsQueryFilters;
  onSetFilters: (filters: GetParentsQueryFilters) => void;
};

export const useParentsFilters = ({ schoolId, filters, onSetFilters }: UseParentsFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Student, // Parents use student age groups
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { hasHouses } = useSchool();
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const updateFilter =
      (key: FilterKeys.House | FilterKeys.AgeGroup | FilterKeys.Status | FilterKeys.Group) =>
      (v: string[] | number[]) => {
        onSetFilters({
          ...filters,
          [key]: v,
        });
      };
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetNationality = (v: Nationalities[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Nationality]: v,
      });
    };
    const onSetGender = (v: Genders[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Gender]: v,
      });
    };
    const onSetInviteStatus = (v: InviteStatus[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.InviteStatus]: v,
      });
    };

    return {
      onSetDate: (v: Date) =>
        onSetFilters({ ...filters, [FilterKeys.Date]: [format(v, DEFAULT_DATE_FORMAT_FNS)] }),
      onSetGender,
      onSetNationality,
      onSetInviteStatus,
      onSetHouseId: updateFilter(FilterKeys.House),
      onClearHouse: clearFilter(FilterKeys.House),
      onSetAgeGroup: updateFilter(FilterKeys.AgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetStatusId: updateFilter(FilterKeys.Status),
      onClearStatus: clearFilter(FilterKeys.Status),
      onSetOnlyEmptyEmail: (v: boolean) => {
        onSetFilters({
          ...filters,
          [FilterKeys.OnlyEmptyEmail]: v ? [1] : [],
        });
      },
      onClearOnlyEmptyEmail: clearFilter(FilterKeys.OnlyEmptyEmail),
      onClearNationality: clearFilter(FilterKeys.Nationality),
      onClearGender: clearFilter(FilterKeys.Gender),
      onClearInviteStatus: clearFilter(FilterKeys.InviteStatus),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date?.[0];
  const filtersStatus = filters.status;
  const filtersNationality = filters.nationality;
  const filtersGender = filters.gender;
  const filtersHouse = filters.house;
  const filtersAgeGroup = filters.age_group;
  const filtersGroup = filters.group;
  const filtersInviteStatus = filters.invite_status;
  const filtersOnlyEmptyEmail = filters.empty_email_only;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Date' }),
    [FilterKeys.Status]: formatMessage({ id: 'schoolProperty-Status' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.House]: formatMessage({ id: 'schoolProperty-House' }),
    [FilterKeys.Gender]: formatMessage({ id: 'peopleDetail-Gender' }),
    [FilterKeys.Nationality]: formatMessage({ id: 'peopleDetail-Nationality' }),
    [FilterKeys.InviteStatus]: formatMessage({ id: 'schoolProperty-InviteStatus' }),
    [FilterKeys.OnlyEmptyEmail]: formatMessage({ id: 'peopleDetail-NoEmail' }),
  };

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...ageGroups,
    ...actions,
    labels,
    hasHouses,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersStatus,
    filtersNationality,
    filtersGender,
    filtersHouse,
    filtersGroup,
    filtersInviteStatus,
    filtersOnlyEmptyEmail,
  };
};
